import { Command } from 'commander';
import { readFileSync } from 'fs';
import { confirm, input } from '@inquirer/prompts';
import { ConnectContextService } from '../../../services/connect-context.service.js';
import { ConnectPolicyService } from '../../../services/connect-policy.service.js';
import { ConnectSqlSafetyService } from '../../../services/connect-sql-safety.service.js';
import { ConnectExecService } from '../../../services/connect-exec.service.js';
import { assertEnvName } from '../shared.js';

interface QueryOptions {
    sql?: string;
    file?: string;
    reason?: string;
    confirmEnv?: string;
    json?: boolean;
}

export function createConnectDbQueryCommand(): Command {
    return new Command('query')
        .description('Run a SQL query with policy and safety checks')
        .argument('<env>', 'Environment')
        .argument('<connection>', 'Connection name (mysql|postgres)')
        .option('--sql <sql>', 'SQL query text')
        .option('--file <path>', 'Path to SQL file')
        .option('--reason <text>', 'Reason for non-read operation')
        .option('--confirm-env <name>', 'Explicit confirmation text for protected envs')
        .option('-j, --json', 'Output JSON')
        .action(async function (this: Command, envRaw: string, connectionName: string, options: QueryOptions) {
            const env = assertEnvName(envRaw);
            const useJson = !!(options.json || this.optsWithGlobals().json);

            const sql = resolveSqlInput(options);
            const context = new ConnectContextService();
            const connection = context.getConnection(env, connectionName);
            if (!connection) throw new Error(`Connection '${connectionName}' not found in ${env}`);
            if (!['mysql', 'postgres'].includes(connection.runtime)) {
                throw new Error(`Connection '${connection.name}' is not a database runtime`);
            }

            const sqlSafety = new ConnectSqlSafetyService();
            const classification = sqlSafety.classify(sql);
            if (classification.operation === 'dangerous') {
                throw new Error(`SQL blocked: ${classification.reason || 'dangerous operation'}`);
            }

            const settings = context.getSettings();
            const policyService = new ConnectPolicyService(settings);
            const policy = policyService.evaluate(env, connection.runtime, classification.operation);
            if (!policy.allowed) {
                throw new Error(`Permission denied: ${policy.reason}`);
            }

            await enforceConfirmation({
                env,
                operation: classification.operation,
                requireConfirm: policy.requireConfirm || env === 'production' || (env === 'staging' && classification.operation !== 'read'),
                requireReason: policy.requireReason || classification.operation !== 'read',
                confirmEnv: options.confirmEnv,
                reason: options.reason,
            });

            const output = new ConnectExecService().runDbQuery(connection, sql);
            if (useJson) {
                console.log(JSON.stringify({
                    env,
                    connection: connection.name,
                    runtime: connection.runtime,
                    operation: classification.operation,
                    allowed: true,
                    output,
                }, null, 2));
                return;
            }

            process.stdout.write(output);
        });
}

function resolveSqlInput(options: QueryOptions): string {
    const hasSql = !!options.sql;
    const hasFile = !!options.file;
    if (hasSql === hasFile) {
        throw new Error('Provide exactly one of --sql or --file');
    }
    if (options.sql) return options.sql;
    return readFileSync(options.file!, 'utf-8');
}

async function enforceConfirmation(inputOptions: {
    env: string;
    operation: string;
    requireConfirm: boolean;
    requireReason: boolean;
    confirmEnv?: string;
    reason?: string;
}): Promise<void> {
    if (inputOptions.requireReason && !inputOptions.reason) {
        if (process.stdin.isTTY && process.stdout.isTTY) {
            inputOptions.reason = await input({ message: `Reason for ${inputOptions.operation} on ${inputOptions.env}:` });
        }
        if (!inputOptions.reason) {
            throw new Error('Reason is required for this operation. Use --reason "<text>".');
        }
    }

    if (!inputOptions.requireConfirm) return;
    if (inputOptions.confirmEnv === inputOptions.env) return;

    if (process.stdin.isTTY && process.stdout.isTTY) {
        const ok = await confirm({
            message: `Confirm ${inputOptions.operation} on ${inputOptions.env}?`,
            default: false,
        });
        if (!ok) {
            throw new Error('Cancelled by user');
        }
        return;
    }

    throw new Error(`Confirmation required. Re-run with --confirm-env ${inputOptions.env}`);
}
